// Напускането на листа „Инвентаризация" — общото `useWorksheetLeave`. Нова
// инвентаризация: черновата се пази сама (браузър + сървър) → излизаме тихо,
// като я записваме веднага; продължава се от началния екран. Редакция:
// незаписаното се губи → червеният въпрос с рецептите и преброеното в листа.
import type { MutableRefObject } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import type { ConfirmPreviewItem } from "../../components/confirm/confirmTypes";
import { useWorksheetLeave } from "../../components/drafts/useWorksheetLeave";
import { recipePreviewItems } from "./stocktakeDraft";
import type { StocktakeRecipeEntry } from "./stocktakeRecipes";
import type { StocktakeComputedRow } from "./stocktakeWorksheetShared";

type Params = {
  mode: "new" | "edit";
  countedRows: StocktakeComputedRow[];
  // приготвените рецепти в листа
  recipeEntries: StocktakeRecipeEntry[];
  dirtyRef: MutableRefObject<boolean>;
  flushDraft: () => Promise<void>;
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

// „Продукт — намерено" на преброените редове (въпросите за напускане и триене)
export const countedPreviewItems = (countedRows: StocktakeComputedRow[]): ConfirmPreviewItem[] =>
  countedRows.map((row) => ({
    key: row.product.id,
    label: row.product.name,
    value: formatQtyUnit(row.countedQty ?? 0, row.decimals, row.unit),
  }));

export const useStocktakeLeave = ({
  mode,
  countedRows,
  recipeEntries,
  dirtyRef,
  flushDraft,
  onRegisterLeaveGuard,
}: Params) => {
  const { t } = useTranslation();
  const { stocktakeId } = useParams();
  return useWorksheetLeave({
    mode,
    dirtyRef,
    flushDraft,
    onRegisterLeaveGuard,
    describeUnsaved: () => ({
      document:
        mode === "new"
          ? t("inventoryPage.cfmDocNewStocktake")
          : t("inventoryPage.cfmDocEditStocktake", { number: stocktakeId ?? "" }),
      isNew: mode === "new",
      items: [...recipePreviewItems(t, recipeEntries), ...countedPreviewItems(countedRows)],
    }),
  });
};
